import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/hooks/useLenis";

const LINKS = [
  { id: "about", label: "About" },
  { id: "experience", label: "Experience" },
  { id: "highlights", label: "Highlights" },
  { id: "gallery", label: "Gallery" },
  { id: "booking", label: "Booking" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (onHome) {
      scrollToId(id);
    } else {
      navigate("/");
      setTimeout(() => scrollToId(id), 400);
    }
  };

  return (
    <>
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.76, 0, 0.24, 1] }}
        className={`fixed top-0 inset-x-0 z-[80] transition-colors duration-500 ${scrolled ? "glass" : "bg-transparent border-transparent"}`}
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex items-center justify-between h-[68px]">
          <button
            data-testid="nav-logo"
            onClick={() => (onHome ? scrollToId("hero") : navigate("/"))}
            className="transition-opacity duration-300 hover:opacity-80"
          >
            <img src="/media/logo-white.avif" alt="DaniQ" className="h-6 w-auto" />
          </button>

          <div className="hidden md:flex items-center gap-8">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => go(l.id)}
                className="relative text-sm uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors duration-300 group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-magenta group-hover:w-full transition-all duration-300" />
              </button>
            ))}
            <button
              data-testid="nav-producer"
              onClick={() => { setOpen(false); navigate("/producer"); }}
              className="rounded-full border border-gold/60 px-5 py-2 text-xs uppercase tracking-[0.2em] text-gold hover:bg-gold hover:text-ink-950 transition-colors duration-300"
            >
              Producer
            </button>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden text-white"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </motion.nav>

      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-[79] md:hidden bg-ink-950/95 backdrop-blur-xl flex flex-col items-center justify-center gap-8"
        >
          {LINKS.map((l, i) => (
            <motion.button
              key={l.id}
              data-testid={`nav-mobile-${l.id}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * i }}
              onClick={() => go(l.id)}
              className="font-display text-5xl tracking-[0.15em] text-white/90 hover:text-magenta"
            >
              {l.label}
            </motion.button>
          ))}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            data-testid="nav-mobile-producer"
            onClick={() => { setOpen(false); navigate("/producer"); }}
            className="mt-2 rounded-full border border-gold/60 px-8 py-3 text-sm uppercase tracking-[0.2em] text-gold"
          >
            Producer / Trance
          </motion.button>
        </motion.div>
      )}
    </>
  );
};
